import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography, typographyStyle} from '@/components/Typography';
import {KODATA_MAP_CATEGORIES} from '@/constants';
import {KodataCategory} from '@/enums';
import {
  AnalysisItem,
  KodataCategoryGroup,
  KodataCustomerGenderAgeData,
} from '@/map/@types';
import {useCustomerGenderAge, useMapDataSource} from '@/map/api';
import {AgeGenderRadarChart} from '@/map/components/Chart';
import {AGE_GENDER_SUBJECTS} from '@/map/constants';
import {ColumnFlexBox, FlexCenter, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {CategoryTabs} from './CategoryTabs';
import {ErrorBox} from './ErrorBox';
import {NoData} from './NoData';
import {ReportContainer, SectionLabel} from './StyledComponents';
import {useGA4WithParams} from '@/hooks/tracking';
import {mapAgentTypeAtom} from '@/map/atom';
import {useAtomValue} from 'jotai';
import {AreaOverlayType} from '@/map/enums';

interface IProps {
  item: AnalysisItem;
}

const categories = Object.values(KodataCategory);

export const Customer = ({item}: IProps) => {
  if (!item.circle?.radius || item.circle.radius > 1_000) {
    return (
      <PaddingBox top={28}>
        <NoData text="고객 데이터를 조회할 수 없어요.<br />분석 반경을 1km이하로 변경해주세요." />
      </PaddingBox>
    );
  }

  return <Report item={item} />;
};

const Report = ({item}: IProps) => {
  const [active1depth, setActive1depth] = useState<KodataCategory>(
    categories[0]
  );
  const cat2depth = useMemo<KodataCategoryGroup[]>(() => {
    if (!active1depth) return [];

    const cat: string[] = KODATA_MAP_CATEGORIES[active1depth] ?? [];

    return cat.map((i) => ({
      category: active1depth,
      categoryDetail: i,
    }));
  }, [active1depth]);
  const {data, isPending, error} = useCustomerGenderAge(item, cat2depth);

  const mapAgent = useAtomValue(mapAgentTypeAtom);

  const tracking = useGA4WithParams();

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const {key} = e.currentTarget.dataset;

    if (key) {
      setActive1depth(key as KodataCategory);
      tracking('demographicPayment_circle_mobile', {
        radius: item?.[AreaOverlayType.CIRCLE]?.radius,
        businessCategory: key,
        agent: mapAgent,
      });
    }
  };

  const emptyData =
    !data || !data.maleAgeGender?.length || !data.femaleAgeGender?.length;

  return (
    <>
      <CategoryTabs
        categories={categories}
        activeTab={active1depth}
        onClick={onClick}
      />
      <ReportContainer>
        <Content>
          {!!error ? (
            <ErrorBox />
          ) : isPending ? (
            <ColumnFlexBox gap={16}>
              <LoadingBar color={palette.textSecondary} />
              <FlexCenter>
                <Typography variant="paragraph14" align="center">
                  고객 데이터를 불러오고 있어요.
                  <br />
                  반경 범위를 줄이면 더 빠르게 결과를 확인할 수 있어요!
                </Typography>
              </FlexCenter>
            </ColumnFlexBox>
          ) : emptyData ? (
            <NoData text="조회하신 지역은 고객 데이터가 없어요." />
          ) : (
            <Data data={data} />
          )}
        </Content>
      </ReportContainer>
    </>
  );
};

const Data = ({data}: {data: KodataCustomerGenderAgeData}) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const summary = useMemo(() => {
    if (!data) return;

    const getMaxAgeInfo = (values: number[]) => {
      const maxValue = Math.max(...values);
      const ages = values
        .map((value, idx) => ({value, age: AGE_GENDER_SUBJECTS[idx]}))
        .filter(({value}) => value === maxValue)
        .map(({age}) => age);

      return {maxValue, ages};
    };

    const male = getMaxAgeInfo(data.maleAgeGender);
    const female = getMaxAgeInfo(data.femaleAgeGender);

    const formatMaxString = (ages: string[], gender: '남성' | '여성') =>
      ages.map((age) => `${age} ${gender}`).join(', ');

    return male.maxValue === female.maxValue
      ? [
          formatMaxString(male.ages, '남성'),
          formatMaxString(female.ages, '여성'),
        ].join(', ')
      : formatMaxString(
          male.maxValue > female.maxValue ? male.ages : female.ages,
          male.maxValue > female.maxValue ? '남성' : '여성'
        );
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <ColumnFlexBox gap={18}>
      <ColumnFlexBox gap={3}>
        <SectionLabel>결제 비중(성연령별)</SectionLabel>
        <Typography variant="medium12" color={palette.gray400}>
          {`${metadata?.source || '카드사'}∙${metadata?.baseDate || ''}`}
        </Typography>
      </ColumnFlexBox>
      {!!summary?.length && (
        <SummaryBox>
          <Typography color={palette.primary500} fontWeight="700">
            조회하신 지역
          </Typography>
          은 <strong>{summary}</strong>의 결제 비중이 가장 높아요.
        </SummaryBox>
      )}
      <AgeGenderRadarChart data={data} />
    </ColumnFlexBox>
  );
};

const Content = styled.div``;

const SummaryBox = styled.div`
  border-radius: 5px;
  padding: 8px;
  border: 1px solid ${palette.primary200};
  outline: 3px solid ${palette.primary100};
  color: ${palette.gray600};
  ${{...typographyStyle.medium12}}
`;
